import type { Bundle, Item, NormalizedProductReview, ProductReview } from 'types';
import Reviews from 'components/Reviews';
import StarRating from 'components/StarRating';
import productsApi from 'services/productsApi';

/**
 * Returns a page of reviews for the product with the given ID
 *
 * This is called on the client side when a user clicks the "load more reviews"
 * button. See `getInitialProductReviews()` for getting the first page of reviews
 * at build time.
 */
export const getProductReviews = async (id: number, page = 1): Promise<NormalizedProductReview> => {
  const response = await productsApi.getProductReviews(page, id);

  const { reviews } = response.productReviews;
  const { averageScore, totalReview } = response.productReviews.bottomLine;

  return {
    data: reviews,
    totalReviewCount: totalReview,
    averageStarRating: averageScore,
    page: page,
  };
};

/**
 * Returns the first page of product reviews for the given item
 *
 * This is primarily used when statically generating a bundle's product page.
 * See `getProductReviews()` for fetching subsequent pages on the client side.
 */
export const getInitialProductReviews = (product: Item, isBundle: boolean) => {
  if (!isBundle) {
    return {
      page: 0,
      data: [],
      totalReviewCount: 0,
      averageStarRating: 0,
    };
  }

  return {
    page: 1,
    data: (product as Bundle).productReviews!.reviews,
    totalReviewCount: (product as Bundle).productReviews!.bottomLine.totalReview,
    averageStarRating: (product as Bundle).productReviews!.bottomLine.averageScore,
  };
};

const formatReviews = (reviews: ProductReview[]) =>
  reviews.map((review) => ({
    ...review,
    content: review.content.replace(/&#x27;/g, "'"),
    createdAt: new Date(review.createdAt).getTime(),
  }));

type Props = {
  onClickLoadMore: () => void;
  reviews: NormalizedProductReview;
  [key: string]: any;
};

const ProductReviews = (props: Props) => {
  const { onClickLoadMore } = props;
  const { data: reviews, totalReviewCount, averageStarRating } = props.reviews;

  if (reviews.length === 0) {
    return null;
  }

  return (
    <div {...props} id="product-reviews">
      <div className="container">
        <div className="row">
          <div className="col-span-12 sm:col-span-4">
            <div className="mb-32 space-y-16 bg-white p-32">
              <h2 className="text-h2-display">Customer Reviews</h2>

              <div className="space-y-4">
                <h4 className="text-h4">Overall</h4>

                <StarRating rating={averageStarRating} />

                <p className="text-sm">({totalReviewCount}&nbsp;reviews)</p>
              </div>
            </div>
          </div>

          <div className="col-span-12 sm:col-span-8">
            <Reviews
              reviews={formatReviews(reviews)}
              canLoadMore={reviews.length !== totalReviewCount}
              loadMoreClicked={onClickLoadMore}
              customFields={[
                { name: 'Fit', key: 'OVERALL FIT' },
                { name: 'Experience', key: 'EXPERIENCE' },
                { name: 'Quality', key: 'QUALITY' },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductReviews;
